import { message } from 'antd';
import axios from 'axios';
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useSWR from 'swr';
import { AuthBox, AuthWrapper, Form, NextBtn } from '../../../styles/Qstyle';
import { ThemeContext } from '../../../theme/ThemeProvider';

const Contents = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
`;

const MainTitle = styled.p`
  color: ${({ theme }) => theme.text};
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 38.19px;
  text-align: center;
  margin-bottom: 42px;
`;

const ColorTitle = styled.span`
  color: ${({ theme }) => theme.primary};
  font-weight: 800;
`;

const TitleWrapper = styled.div`
  margin-bottom: 36px;
`;

const FormTitle = styled.div`
  font-weight: 600;
  font-size: 32px;
  line-height: 38.19px;
  color: ${({ theme }) => theme.text};
  margin-bottom: 12px;
`;

const FormSubTitle = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 19.09px;
  color: ${({ theme }) => theme.text};
`;

const AdditionalInfoPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {  isDarkMode } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  const [token, setToken] = useState('');

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');

    if (!tokenParam) {
      message.error('Token is missing');
      return;
    }

    setToken(tokenParam);
  }, [navigate]);

  const [loading, setLoading] = useState(false);
  const [referral, setReferral] = useState('');
  const [referralError, setReferralError] = useState('');

  // 레퍼럴 코드 검증 API 호출
  const { data: referralValidation, error: validationError } = useSWR(
    referral.length >= 1 ? `/referral/valid?code=${referral}` : null,
  );

  // 레퍼럴 코드 검증 결과 처리
  useEffect(() => {
    if (referral.length === 0) {
      setReferralError('');
    } else if (referral.length >= 1) {
      if (referralValidation && referralValidation?.result === false) {
        const currentLanguage = i18n.language;
        if (currentLanguage === 'ko') {
          setReferralError('유효하지 않은 추천인 코드입니다');
        } else {
          setReferralError('Invalid referral code');
        }
      } else if (validationError) {
        const currentLanguage = i18n.language;
        if (currentLanguage === 'ko') {
          setReferralError('추천인 코드 검증에 실패했습니다');
        } else {
          setReferralError('Failed to validate referral code');
        }
      } else if (referralValidation && referralValidation?.result === true) {
        setReferralError('');
      }
    }
  }, [referralValidation, validationError, referral.length, i18n.language]);

  const handleSubmit = async () => {
    // 레퍼럴 코드 필수 입력 검증
    if (!referral) {
      const currentLanguage = i18n.language;
      if (currentLanguage === 'ko') {
        message.error('추천인 코드를 입력해 주세요');
      } else {
        message.error('Please enter your referral code');
      }
      return;
    }

    // 레퍼럴 코드 유효성 검증
    if (referralError) {
      message.error(referralError);
      return;
    }

    // 레퍼럴 코드 검증이 아직 완료되지 않은 경우
    if (referral.length >= 1 && !referralValidation) {
      const currentLanguage = i18n.language;
      if (currentLanguage === 'ko') {
        message.error('추천인 코드 검증 중입니다. 잠시 후 다시 시도해주세요');
      } else {
        message.error('Validating referral code. Please try again in a moment');
      }
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_APP_API_URL}/auth/google/complete-signup`,
        {
          token: token,
          referral: referral,
        }
      );

      if (response.data.success) {
        message.success(t('success'));
        navigate('/');
      } else {
        // msgcode: 8 - 레퍼럴 코드 오류 처리
        if (response.data.msgcode === 8) {
          const currentLanguage = i18n.language;
          if (currentLanguage === 'ko') {
            message.error('유효하지 않은 추천인 코드입니다');
          } else {
            message.error('Invalid referral code');
          }
        } else {
          message.error(response.data.message || 'An error occurred');
        }
      }
    } catch (error) {
      // message.error(t('error'));
      // console.error('Additional info error:', error);
      message.success(t('success'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthWrapper>
      <Contents>
        <AuthBox>
          <MainTitle>
            {t("signUp")} <ColorTitle>FossX</ColorTitle>
          </MainTitle>

          <TitleWrapper>
            <FormTitle>{t("additionalInfo")}</FormTitle>
            <FormSubTitle>{t("additionalInfoDescription")}</FormSubTitle>
          </TitleWrapper>

          <Form>
            <input
              type="text"
              id="referral"
              placeholder={t("referralCode")}
              value={referral}
              onChange={(e) => setReferral(e.target.value)}
            />

            <NextBtn
              style={{ cursor: 'pointer' }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {t("complete")}
            </NextBtn>
          </Form>
        </AuthBox>
      </Contents>
    </AuthWrapper>
  );
};

export default AdditionalInfoPage;
